import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react'

import {Alert, Button, Modal, ModalActions, ModalHeader, S2, Toast} from '../UI/index.js'
import type {ToastHandle} from '../UI/index.js'
import {FormStack, Input} from '../UI/forms/index.js'
import Address from '../addresses/Address.js'
import {PasswordInput, PasswordRequirements, passwordReady} from './PasswordFields.js'
import t from 'src/helpers/Languages.js'

type Callback<T = undefined> = (err?: string, result?: T) => void

type OwnDetails = {
    name: string
    email: string
    newEmail: string
    isCustomer: boolean
    address?: Record<string, unknown>
    phone?: string
}

/** The calls of the app the menu is in: the web app and the courier app each have their own socket and token. */
export interface AccountApi {
    getOwnDetails: (callback: Callback<OwnDetails>) => void
    updateOwnDetails: (details: {address?: unknown, phone?: string, email?: string}, callback: Callback<string>) => void
    changePassword: (currentPassword: string, newPassword: string, callback: Callback) => void
}

export interface AccountModalsHandle {
    openDetails: () => void
    openPassword: () => void
}

function DetailsModal({api, onClose}: {api: AccountApi, onClose: () => void}) {
    const [details, setDetails] = useState<OwnDetails | null>(null)
    // Address with the email and phone in it, the way the address form edits them
    const [address, setAddress] = useState<Record<string, unknown> | null>(null)
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [sentTo, setSentTo] = useState('')

    React.useEffect(() => {
        api.getOwnDetails((err, result) => {
            setLoading(false)

            if (err) {
                setError(err)
                return
            }

            setDetails(result)
            setEmail(result.email)
            setAddress(result.isCustomer ? null : {...result.address, name: result.name, email: result.email, phone: result.phone || ''})
        })
    }, [])

    const save = () => {
        setLoading(true)
        setError('')

        const newEmail = (details.isCustomer ? email : address.email as string) || ''
        const changes = details.isCustomer ?
            {email: newEmail} :
            {address, phone: (address.phone as string) || '', email: newEmail}

        api.updateOwnDetails(changes, (err, emailConfirmationSentTo) => {
            setLoading(false)

            if (err) {
                setError(err)
            } else if (emailConfirmationSentTo) {
                setSentTo(emailConfirmationSentTo)
            } else {
                onClose()
            }
        })
    }

    return (
        <Modal show onClose={onClose} style={{width: details?.isCustomer || sentTo ? 420 : 640}}>
            <ModalHeader title={t('accountDetails')} onClose={onClose} />

            {sentTo ?
                <>
                    <Alert variant='info'>{t('accountDetailsConfirmationSent', sentTo)}</Alert>
                    <ModalActions>
                        <Button label={t('close')} onClick={onClose} />
                    </ModalActions>
                </> :
                <>
                    {details && (details.isCustomer ?
                        <FormStack>
                            <Input label={t('name')} value={details.name} readOnly />
                            <Input label={t('emailAddress')} type='email' value={email} onChange={(event) => setEmail(event.target.value.toLowerCase().replace(/\s+/g, ''))} />
                        </FormStack> :
                        address &&
                        <Address
                            address={address}
                            noTAV
                            nameReadOnly
                            email
                            phone
                            onChange={(changed) => setAddress({...changed, name: details.name})}
                        />
                    )}

                    {details?.newEmail && !error && <Alert variant='info' style={{marginTop: 12}}>{t('accountDetailsPendingEmail', details.newEmail)}</Alert>}
                    {error && <Alert variant='danger' style={{marginTop: 12}}>{error}</Alert>}

                    {details &&
                        <S2 style={{marginTop: 12}}>{details.isCustomer ? t('accountDetailsEmailNote') : `${t('accountDetailsNameNote')} ${t('accountDetailsEmailNote')}`}</S2>
                    }

                    <ModalActions style={{marginTop: 24}}>
                        <Button variant='text' label={t('cancel')} onClick={onClose} />
                        <Button label={t('save')} loading={loading} disabled={!details} onClick={save} />
                    </ModalActions>
                </>
            }
        </Modal>
    )
}

function PasswordModal({api, onClose, onChanged}: {api: AccountApi, onClose: () => void, onChanged: () => void}) {
    const [current, setCurrent] = useState('')
    const [password, setPassword] = useState('')
    const [repeat, setRepeat] = useState('')
    const [loading, setLoading] = useState(false)
    const [currentError, setCurrentError] = useState('')
    const [error, setError] = useState('')

    const save = () => {
        setLoading(true)
        setCurrentError('')
        setError('')

        api.changePassword(current, password, (err) => {
            setLoading(false)

            if (!err) {
                onChanged()
            } else if (err === t('wrongCurrentPassword')) {
                setCurrentError(err)
            } else {
                setError(err)
            }
        })
    }

    return (
        <Modal show onClose={onClose} style={{width: 420}}>
            <ModalHeader title={t('changePassword')} onClose={onClose} />

            <FormStack>
                <PasswordInput
                    label={t('currentPassword')}
                    autoComplete='current-password'
                    value={current}
                    isInvalid={currentError}
                    onChange={(value) => {
                        setCurrent(value)
                        setCurrentError('')
                    }}
                />
                <PasswordInput label={t('newPassword')} value={password} onChange={setPassword} />
                <PasswordInput label={t('repeatNewPassword')} value={repeat} isInvalid={!!repeat && repeat !== password} onChange={setRepeat} />
            </FormStack>

            <div style={{marginTop: 18}}>
                <PasswordRequirements password={password} repeat={repeat} currentPassword={current} />
            </div>

            {error && <Alert variant='danger' style={{marginTop: 12}}>{error}</Alert>}

            <ModalActions style={{marginTop: 24}}>
                <Button variant='text' label={t('cancel')} onClick={onClose} />
                <Button label={t('save')} loading={loading} disabled={!current || !passwordReady(password, repeat, current)} onClick={save} />
            </ModalActions>
        </Modal>
    )
}

/**
 * Gegevens and Wachtwoord wijzigen of the account menu (#602), for the web app, the customer portal and the courier
 * app. Opened through the ref; not offered while logged in as a customer or carrier.
 */
const AccountModals = forwardRef<AccountModalsHandle, {api: AccountApi}>(function AccountModals({api}, ref) {
    const [open, setOpen] = useState<'details' | 'password' | null>(null)
    const toast = useRef<ToastHandle>(null)

    useImperativeHandle(ref, () => ({
        openDetails: () => setOpen('details'),
        openPassword: () => setOpen('password')
    }))

    return (
        <>
            {open === 'details' && <DetailsModal api={api} onClose={() => setOpen(null)} />}
            {open === 'password' &&
                <PasswordModal
                    api={api}
                    onClose={() => setOpen(null)}
                    onChanged={() => {
                        setOpen(null)
                        toast.current?.showToast('success', t('passwordChanged'))
                    }}
                />
            }
            <Toast ref={toast} />
        </>
    )
})

export default AccountModals

/** Whether the menu offers Gegevens and Wachtwoord wijzigen: only in the own account. */
export function canEditOwnAccount(user: {impersonatedBy?: string, loggedInAsReseller?: boolean, isAdmin?: boolean} | undefined) {
    return !!user && !user.impersonatedBy && !user.loggedInAsReseller && !user.isAdmin
}
